import { useLayoutEffect, useRef, useState } from 'react';

import {
    useDeleteAdminConversationCategory,
    useUpdateAdminConversationCategory,
    useUpdateSortAdminConversationCategory,
} from '@entities/AdminConversationCategory';
import { ICategory, IForm } from '@entities/AdminConversationCategory/model/types';
import { useDialog } from '@shared/hooks';

export const useAdminConversationCategory = (category: ICategory) => {
    const { mutateAsync } = useUpdateAdminConversationCategory();
    const { mutateAsync: mutateAsyncDeleteAdminConversationCategory } =
        useDeleteAdminConversationCategory();
    const { mutateAsync: mutateAsyncUpdateSortAdminConversationCategory } =
        useUpdateSortAdminConversationCategory();

    const [formId, setFormId] = useState(null);
    const [prevData, setPrevData] = useState<IForm | null>(null);

    const [isShowForms, setIsShowForms] = useState(false);
    const [maxHeight, setMaxHeight] = useState<string | number>(0);
    const contentRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (isShowForms && contentRef.current) {
            setMaxHeight(contentRef.current.scrollHeight);
        } else {
            setMaxHeight(0);
        }
    }, [isShowForms, category.items]);

    const {
        open: openCategoryUpsertModal,
        isOpen: isOpenCategoryUpsertModal,
        close: closeCategoryUpsertModal,
    } = useDialog();

    const handleToggle = () => {
        if (isShowForms) {
            setMaxHeight(0);
            setIsShowForms((prevState) => !prevState);
        } else {
            setMaxHeight(contentRef.current.scrollHeight);
            setIsShowForms((prevState) => !prevState);
        }
    };

    const onTitleChange = async (value: string) => {
        const formData = new FormData();
        formData.append('title', value);
        formData.append('id', `${category.id}`);
        await mutateAsync({ formData, id: category.id });
    };

    const onDeleteCategory = async () => {
        await mutateAsyncDeleteAdminConversationCategory(category.id);
    };

    const onSortChange = async (type: 'up' | 'down') => {
        await mutateAsyncUpdateSortAdminConversationCategory({
            id: category.id,
            type,
        });
    };

    const onEditForm = (currentFormId: number) => {
        setFormId(currentFormId);
        openCategoryUpsertModal();
    };

    const onShowPrev = (categoryItem: IForm) => {
        setPrevData(categoryItem);
    };
    const onHidePrev = () => {
        setPrevData(null);
    };

    const onCloseModal = () => {
        closeCategoryUpsertModal();
        setFormId(null);
    };

    return {
        handleToggle,
        onTitleChange,
        onSortChange,
        onDeleteCategory,
        isShowForms,
        maxHeight,
        contentRef,
        openCategoryUpsertModal,
        isOpenCategoryUpsertModal,
        onCloseModal,
        formId,
        onEditForm,
        prevData,
        onShowPrev,
        onHidePrev,
    };
};
